"use client";

import { useRef, useEffect, useState } from "react";
import Image from "next/image";
import { ArrowRight } from "@phosphor-icons/react";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";
import type { Venue } from "@/lib/venues";

export default function VenueBookingHero({ venues }: { venues: Venue[] }) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [, isDesktop, transform3d] = useParallaxOffset(0.2, 640, parallaxRef);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative overflow-hidden text-white">
      {/* Parallax image behind everything */}
      <div
        ref={parallaxRef}
        className="absolute will-change-transform w-full -top-[10%] h-[120%]"
        style={mounted && isDesktop ? { transform: transform3d } : {}}
      >
        <Image
          src="https://images.ctfassets.net/78dn8eg8o3k3/5OK7EgfNQfntK3gxiKMJIL/87738df2322f6fb2bcd66dc074167cab/legacy-hall.png"
          alt="Legacy Hall venue"
          fill
          className="object-cover object-center"
          priority
          sizes="100vw"
        />
      </div>

      {/* Original gradient — now with transparency */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgb(4 16 63 / 0.85) 0%, rgb(11 27 98 / 0.82) 58%, rgb(22 48 141 / 0.78) 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at top right, rgba(228, 203, 67, 0.18), transparent 24%), radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.08), transparent 30%)",
        }}
      />
      <div className="absolute -right-24 top-10 h-64 w-64 rounded-full bg-school-gold/15 blur-3xl" />
      <div className="absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-5xl gap-10 px-4 py-18 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:items-end">
        <div className="max-w-3xl pt-8 lg:pt-12">
          <h1 className="mt-5 max-w-2xl text-4xl font-extrabold leading-[1.02] tracking-[-0.03em] text-white md:text-6xl">
            Simple booking for standout school spaces.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-blue-100/90 md:text-lg">
            Our halls, sports fields, and dining facilities are available to
            the community outside of school hours. Submit a request, receive a
            personalised quote, and once you&apos;re happy we&apos;ll confirm
            your booking and send you a confirmation email.
          </p>

          <div className="mt-8 flex flex-col gap-3 mb-8 sm:flex-row sm:items-center">
            <a
              href="#booking-calendar"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-school-gold px-5 py-3 text-sm font-bold text-school-navy transition hover:bg-school-gold-dark"
            >
              Check availability
              <ArrowRight className="h-4 w-4" weight="bold" />
            </a>
            <p className="text-sm text-blue-100/80">
              We&apos;ll send you a quote within 2 business days.
            </p>
          </div>
        </div>

        <div className="rounded-4xl border border-white/12 bg-white/10 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.28)] backdrop-blur-md md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-school-gold">At a glance</p>
              <h2 className="mt-2 text-2xl font-bold text-white">Spaces ready to book</h2>
            </div>
            <div className="rounded-full border border-white/12 bg-white/10 px-3 py-1 text-xs font-semibold text-blue-100/80">
              {venues.length} venues
            </div>
          </div>

          <div className="mt-6 space-y-3 pb-1">
            {venues.map((venue) => (
              <div
                key={venue.id}
                className="flex items-center justify-between rounded-2xl border border-white/10 bg-school-navy/30 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-bold text-white">{venue.name}</p>
                  <p className="mt-1 text-xs text-blue-100/75">{venue.operatingHours.start} - {venue.operatingHours.end}</p>
                </div>
                <div className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                  Up to {venue.capacity.toLocaleString("en-ZA")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
